import { getWarningText } from '@hungryroot/dietary-restrictions';
import { type DietaryRestrictionsText } from '@hungryroot/dietary-restrictions';
import { WarningTextStyleVariant } from '@hungryroot/pho';

export interface DetailWarning {
  kind: 'dietary' | 'never';
  warningText: string;
  warningTextIntensity: WarningTextStyleVariant;
}

export type DetailWarningScope = 'recipe' | 'item';

/** Truncates name lists like dietary warnings: "A, B + N more". */
export function formatTruncatedNameList(names: string[]): string {
  if (names.length > 2)
    return `${names.slice(0, 2).join(', ')} + ${names.length - 2} more`;

  return names.join(', ');
}

export function getNeverWarningText({
  scope,
  neverTagNames = [],
}: {
  scope: DetailWarningScope;
  neverTagNames?: string[];
}): string {
  if (neverTagNames.length > 0)
    return `Contains ${formatTruncatedNameList(neverTagNames)}`;

  if (scope === 'recipe')
    return `This recipe contains items you set to 'never'`;

  return `You set this item to 'never'`;
}

/**
 * Detail-modal warnings. Default is legacy single-winner (dietary > never).
 * Pass `stackNeverWithDietary` for Exp 195 Food Profile (red then yellow).
 */
export function getDetailWarnings({
  dietaryIngredients,
  hasNever,
  neverTagNames = [],
  scope,
  stackNeverWithDietary = false,
}: {
  dietaryIngredients: DietaryRestrictionsText[];
  hasNever: boolean;
  neverTagNames?: string[];
  scope: DetailWarningScope;
  stackNeverWithDietary?: boolean;
}): DetailWarning[] {
  const warnings: DetailWarning[] = [];

  if (dietaryIngredients.length > 0) {
    const subject = scope === 'recipe' ? 'recipe' : 'item';
    warnings.push({
      kind: 'dietary',
      warningText: `This ${subject} contains ${getWarningText(
        dietaryIngredients
      )}`,
      warningTextIntensity: WarningTextStyleVariant.Critical,
    });

    if (!stackNeverWithDietary) return warnings;
  }

  if (hasNever) {
    warnings.push({
      kind: 'never',
      warningText: getNeverWarningText({ scope, neverTagNames }),
      warningTextIntensity: WarningTextStyleVariant.Moderate,
    });
  }

  return warnings;
}
